import { aboutPages } from './about_pages'
import { canonicalBrowseSearch } from './browse_params'
import { games } from './games'

// Indexable query params for browse grids (home, set index, set, shop, collection).
// Emitted in this order:
// - game — catalog scope. Unknown ids are dropped. #685 keeps game= beside query=.
// - query — search text. `q` is not allowlisted; canonicalBrowseSearch (#685) rewrites
//   it to `query`, and query wins when both are present, so the two spellings share
//   one canonical.
//
// Not allowlisted, so they are omitted from <link rel="canonical">:
// - view, sort
// - facets: stocks, card_types, energy_types, rarities, conditions, min_price,
//   max_price, user_id
// - page — catalog browse does not read it (infinite scroll). #763 owns pagination.
// - tracking (utm_*, fbclid, gclid, …) and ref. ref stays on the address bar because
//   the app records it; it is not indexed.
//
// Card, listing, profile about/reviews/blog, and static pages allow no query
// params. /categories/3178/items/31446?query=pikachu&view=items&game=4100
// canonicalizes to /categories/3178/items/31446.
// /collections/:id is the same profile as /u/:id, so its canonical uses /u/.
// The address bar is left on whichever prefix was opened.
//
// #743 SSR/prerender should call renderPrerenderedHtml() so the tag is in the
// first HTML. The SPA writes the same tag into document.head until then.

const indexableQueryParams = ['game', 'query'] as const

const staticPages = new Set<string>(aboutPages)

const trackingParamNames = new Set([
  'fbclid',
  'gclid',
  'gclsrc',
  'dclid',
  'msclkid',
  'twclid',
  'ttclid',
  'igshid',
  'mc_cid',
  'mc_eid',
  '_ga',
  '_gl',
  'srsltid',
  'yclid',
  'gad_source',
  'gad_campaignid',
  'gbraid',
  'wbraid',
  'vero_id',
])

const CANONICAL_SLOT = '<!-- holic-canonical -->'

type PageKind = 'browse' | 'document' | 'none'

const isId = (value: string | undefined): boolean => !!value && /^\d+$/.test(value)

const cleanPath = (pathname: string): string => {
  const path = pathname.split('?')[0].split('#')[0]
  if (path.length > 1 && path.endsWith('/')) return path.replace(/\/+$/, '')
  return path || '/'
}

const classify = (pathname: string): PageKind => {
  const parts = cleanPath(pathname).split('/').filter(Boolean)

  if (parts.length === 0) return 'browse'

  if (parts[0] === 'categories') {
    if (parts.length === 1) return 'browse'
    if (parts.length === 2 && isId(parts[1])) return 'browse'
    if (parts.length === 4 && parts[2] === 'items' && isId(parts[1]) && isId(parts[3])) return 'document'
    return 'none'
  }

  if (parts[0] === 'u' || parts[0] === 'collections') {
    if (!isId(parts[1])) return 'none'
    if (parts.length === 2) return 'browse'
    if (parts.length === 3 && (parts[2] === 'about' || parts[2] === 'reviews' || parts[2] === 'blog')) return 'document'
    if (parts.length === 4 && parts[2] === 'blog' && isId(parts[3])) return 'document'
    if (parts.length === 4 && parts[2] === 'units' && isId(parts[3])) return 'document'
    return 'none'
  }

  if (parts[0] === 'about' && parts.length === 2 && staticPages.has(parts[1])) return 'document'

  return 'none'
}

const searchParamsFrom = (search: string) => new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

const isTrackingParam = (key: string): boolean => {
  const name = key.toLowerCase()
  return name.startsWith('utm_') || trackingParamNames.has(name)
}

const allowedGame = (raw: string | null): string | undefined => {
  if (!raw || !/^\d+$/.test(raw)) return undefined
  const id = Number(raw)
  if (!games.some(game => game.id === id)) return undefined
  return String(id)
}

const canonicalQuery = (pathname: string, search: string): string => {
  const rewritten = canonicalBrowseSearch(search, pathname)
  const params = searchParamsFrom(rewritten ?? search)
  const next = new URLSearchParams()
  const game = allowedGame(params.get('game'))
  const query = params.get('query')?.trim()

  if (game) next.set('game', game)
  if (query) next.set('query', query)

  return next.toString()
}

const canonicalOrigin = (origin: string): string | undefined => {
  const base = origin.replace(/\/+$/, '')
  if (!/^https?:\/\//i.test(base)) return undefined
  return base
}

const escapeAttr = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')

const canonicalUrl = (pathname: string, search: string, origin: string): string | undefined => {
  const kind = classify(pathname)
  const base = canonicalOrigin(origin)
  if (kind === 'none' || !base) return undefined

  const path = cleanPath(pathname).replace(/^\/collections\//, '/u/')
  const query = kind === 'browse' ? canonicalQuery(pathname, search) : ''

  return query ? `${base}${path}?${query}` : `${base}${path}`
}

const canonicalLinkTag = (pathname: string, search: string, origin: string): string => {
  const href = canonicalUrl(pathname, search, origin)
  if (!href) return ''
  return `<link rel="canonical" href="${escapeAttr(href)}" data-holic-canonical="">`
}

const renderPrerenderedHtml = (html: string, pathname: string, search: string, origin: string): string => {
  const tag = canonicalLinkTag(pathname, search, origin)
  // The homepage shell already has its own canonical. Prerender replaces it
  // with the tag for the requested path so a card page is not canonicalized home.
  const withoutHomeCanonical = html.replace(/<link id="home-canonical"[^>]*>\s*/, '')
  if (withoutHomeCanonical.includes(CANONICAL_SLOT)) return withoutHomeCanonical.replace(CANONICAL_SLOT, tag)
  return withoutHomeCanonical.replace('</head>', `${tag}</head>`)
}

const normalizedAddressSearch = (search: string, pathname: string): string | undefined => {
  const original = searchParamsFrom(search)
  const rewritten = canonicalBrowseSearch(search, pathname)
  const params = searchParamsFrom(rewritten ?? search)
  const removedTracking = Array.from(params.keys()).filter(isTrackingParam)

  removedTracking.forEach(key => params.delete(key))

  if (rewritten === undefined && removedTracking.length === 0) return undefined

  const next = params.toString()
  if (next === original.toString()) return undefined
  return next
}

export {
  CANONICAL_SLOT,
  canonicalLinkTag,
  canonicalUrl,
  indexableQueryParams,
  normalizedAddressSearch,
  renderPrerenderedHtml,
}
